import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { useAdminCompetenceDetail } from '@/lib/admin/queries';
import { useUpdateCompetence, slugify } from '@/lib/admin/mutations';

function tagsToString(arr: string[]): string {
  return arr.join(', ');
}

function stringToTags(s: string): string[] {
  return s
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function linesToArray(s: string): string[] {
  return s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

function arrayToLines(arr: string[]): string {
  return arr.join('\n');
}

export function TutorAdminCompetenceEditorPage() {
  const { competenceId } = useParams({ strict: false }) as { competenceId: string };
  const { data: competence, isLoading } = useAdminCompetenceDetail(competenceId);
  const updateCompetence = useUpdateCompetence();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [displayOrder, setDisplayOrder] = useState(1);
  const [tagsStr, setTagsStr] = useState('');
  const [prerequisStr, setPrerequisStr] = useState('');
  const [erreursStr, setErreursStr] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (competence && !seeded) {
      setTitle(competence.title);
      setSlug(competence.slug);
      setDisplayOrder(competence.display_order);
      setTagsStr(tagsToString(competence.tags));
      setPrerequisStr(tagsToString(competence.prerequis));
      setErreursStr(arrayToLines(competence.erreurs_classiques));
      setSeeded(true);
    }
  }, [competence, seeded]);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugTouched) setSlug(slugify(val));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    await updateCompetence.mutateAsync({
      id: competenceId,
      chapter_id: competence?.chapter_id,
      title: title.trim(),
      slug: slug.trim(),
      display_order: displayOrder,
      tags: stringToTags(tagsStr),
      prerequis: stringToTags(prerequisStr),
      erreurs_classiques: linesToArray(erreursStr),
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  if (!competence) {
    return (
      <div className="p-6 text-center">
        <p className="font-display text-ink-600 font-semibold">Compétence introuvable.</p>
        <Link
          to="/tutor/admin/chapters"
          className="text-primary-600 mt-2 inline-block text-[13px] underline"
        >
          Retour aux chapitres
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      {/* Back */}
      <Link
        to="/tutor/admin/chapters/$chapterId"
        params={{ chapterId: competence.chapter_id }}
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au chapitre
      </Link>

      <form onSubmit={handleSave} className="space-y-4">
        {/* Header */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <h1 className="font-display text-ink-900 truncate text-[22px] font-extrabold">
            {title || 'Compétence sans titre'}
          </h1>
          <Button
            type="submit"
            size="sm"
            disabled={updateCompetence.isPending}
            className="flex shrink-0 items-center gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {saved ? 'Enregistré !' : updateCompetence.isPending ? '...' : 'Enregistrer'}
          </Button>
        </div>

        {/* Title */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            required
          />
        </div>

        {/* Slug + Order */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-mono text-[13px] focus:outline-none focus:ring-2"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-ink-700 text-[12px] font-semibold">Ordre</label>
            <input
              type="number"
              min={1}
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 1)}
              className="border-ink-200 text-ink-900 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Tags <span className="text-ink-400 font-normal">(séparés par des virgules)</span>
          </label>
          <input
            type="text"
            value={tagsStr}
            onChange={(e) => setTagsStr(e.target.value)}
            placeholder="force, vecteur, newton"
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Prérequis */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Prérequis{' '}
            <span className="text-ink-400 font-normal">(slugs, séparés par des virgules)</span>
          </label>
          <input
            type="text"
            value={prerequisStr}
            onChange={(e) => setPrerequisStr(e.target.value)}
            placeholder="grandeurs-scalaires, unites"
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 rounded-xl border px-3 py-2.5 font-mono text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {/* Erreurs classiques */}
        <div className="flex flex-col gap-1.5">
          <label className="font-display text-ink-700 text-[12px] font-semibold">
            Erreurs classiques <span className="text-ink-400 font-normal">(une par ligne)</span>
          </label>
          <textarea
            value={erreursStr}
            onChange={(e) => setErreursStr(e.target.value)}
            rows={5}
            placeholder={"Confondre masse et poids\nOublier l'unité"}
            className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 resize-y rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2"
          />
        </div>

        {updateCompetence.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{updateCompetence.error?.message}</p>
        )}
      </form>
    </div>
  );
}
